// src/components/resources/SupportLines.jsx
// Crisis and support lines for the seven countries the course serves, on the
// Resources page. Everything shown comes from the fleet's one table
// (packages/crisis-support, through src/data/crisisSupport.js): a number,
// description or website changes there, never here.
import React, { useState } from 'react';
import { Phone, MessageCircle, ExternalLink, AlertTriangle, LifeBuoy } from 'lucide-react';
import Card from '../shared/Card';
import {
  CRISIS_SUPPORT,
  DIRECTORIES,
  DEFAULT_COUNTRY,
  detectCountry,
  emergencyCallAdvice
} from '../../data/crisisSupport';

const HEADING_ID = 'support-lines-heading';

// The first phone-shaped run in a number ("999 or 112" → "999", "Triple Zero
// (000)" → "000"), as digits and a leading + for a tel: link.
const dialFor = (number) => number.match(/\+?\d[\d\s().-]*\d|\d/)?.[0].replace(/[^\d+]/g, '') ?? null;

// Only lines you can ring get a tel: link. "Call or text 1737" can be rung;
// "Text SHOUT to 85258" and "Find local branch" are shown as they are.
const telFor = ({ type, number }) => (type === 'call' || /^call\b/i.test(number) ? dialFor(number) : null);

const checkedOn = (isoDate) =>
  new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(`${isoDate}T00:00:00Z`));

const linkClass = 'text-amber-300 hover:text-amber-200 underline underline-offset-2';

// A link to this section from elsewhere on the page. It scrolls to the
// heading and moves focus there, so keyboard and screen-reader users land in
// the section too.
export const SupportLinesLink = ({ children, className = linkClass }) => {
  const jump = (event) => {
    const heading = document.getElementById(HEADING_ID);
    if (!heading) return;
    event.preventDefault();
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    heading.focus({ preventScroll: true });
  };
  return (
    <a href={`#${HEADING_ID}`} onClick={jump} className={className}>
      {children}
    </a>
  );
};

const SupportLines = () => {
  // The reader's region from the browser's language, else Australia. Either
  // way the picker shows which country is on screen and offers the other six.
  const [countryKey, setCountryKey] = useState(() => detectCountry() || DEFAULT_COUNTRY);
  const country = CRISIS_SUPPORT[countryKey];
  const emergencyDial = dialFor(country.emergency.number);

  return (
    <section aria-labelledby={HEADING_ID} className="mt-16">
      <Card hover={false} className="sm:p-8">
        <h2
          id={HEADING_ID}
          tabIndex={-1}
          className="scroll-mt-32 text-2xl md:text-3xl font-bold text-white mb-3 flex items-center gap-3 focus:outline-none"
        >
          <LifeBuoy className="w-7 h-7 text-amber-400 shrink-0" aria-hidden="true" />
          Support lines
        </h2>
        <p className="text-slate-300 mb-6 max-w-3xl">
          The Art of Story is storytelling training, not crisis support. If a story you&apos;re working
          on stirs up something heavy, or you&apos;re worried about someone, these services can help.
          Choose your country:
        </p>

        <div role="group" aria-label="Country" className="flex flex-wrap gap-2 mb-8">
          {Object.entries(CRISIS_SUPPORT).map(([key, { flag, name }]) => (
            <button
              key={key}
              type="button"
              aria-pressed={key === countryKey}
              onClick={() => setCountryKey(key)}
              className={`min-h-[44px] px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                key === countryKey
                  ? 'border-amber-400 bg-amber-500/20 text-white'
                  : 'border-slate-600 text-slate-300 hover:border-slate-400 hover:text-white'
              }`}
            >
              <span aria-hidden="true" className="mr-2">{flag}</span>
              {name}
            </button>
          ))}
        </div>

        <div aria-live="polite">
          <h3 className="text-xl font-bold text-white mb-4">
            <span aria-hidden="true" className="mr-2">{country.flag}</span>
            {country.name}
          </h3>

          <div className="mb-6 p-4 bg-red-900/30 border border-red-500/40 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-300 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-slate-100">
              <strong>In immediate danger?</strong> Call{' '}
              {emergencyDial ? (
                <a href={`tel:${emergencyDial}`} className="font-semibold text-red-200 hover:text-white underline underline-offset-2">
                  {country.emergency.number}
                </a>
              ) : (
                <span className="font-semibold">{country.emergency.number}</span>
              )}{' '}
              now.
            </p>
          </div>

          <ul className="grid md:grid-cols-2 gap-4">
            {country.services.map((service) => {
              const tel = telFor(service);
              return (
                <li key={service.id} className="p-4 bg-slate-900/60 rounded-lg border border-slate-700">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <h4 className="text-lg font-semibold text-white">{service.name}</h4>
                    {service.youthFocused && (
                      <span className="text-xs px-2 py-0.5 rounded-full border border-sky-400/40 text-sky-200">
                        For young people
                      </span>
                    )}
                  </div>
                  <p className="mt-1 flex items-center gap-2 text-lg font-semibold">
                    {tel ? (
                      <Phone className="w-4 h-4 text-amber-400 shrink-0" aria-hidden="true" />
                    ) : (
                      <MessageCircle className="w-4 h-4 text-amber-400 shrink-0" aria-hidden="true" />
                    )}
                    {tel ? (
                      <a href={`tel:${tel}`} className={linkClass}>{service.number}</a>
                    ) : (
                      <span className="text-slate-100">{service.number}</span>
                    )}
                  </p>
                  <p className="text-slate-300 text-sm mt-2">{service.description}</p>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
                    <a
                      href={`https://${service.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1 py-1 break-all ${linkClass}`}
                    >
                      {service.website}
                      <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
                      <span className="sr-only">(opens in a new tab)</span>
                    </a>
                    <span className="text-xs text-slate-400">Checked {checkedOn(service.verifiedOn)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-700 text-sm text-slate-300">
          <p className="mb-3">
            <strong className="text-white">Somewhere else?</strong> {emergencyCallAdvice()} These
            directories list crisis lines in other countries:
          </p>
          <ul className="space-y-2">
            {DIRECTORIES.map((directory) => (
              <li key={directory.id}>
                <a
                  href={`https://${directory.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 py-1 font-medium ${linkClass}`}
                >
                  {directory.name}
                  <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
                <span className="text-slate-400">: {directory.description}</span>
              </li>
            ))}
          </ul>
        </div>
      </Card>
    </section>
  );
};

export default SupportLines;
